/**
 * Presentation helpers for the Verantwortlichkeit (PSIRT) page.
 *
 * The CRA verdict itself is computed server-side (app/services/psirt_service.py)
 * and never re-derived here. The only client-side "rule" is the form checklist,
 * and it deliberately checks *presence* of the fields a requirement needs — it
 * tells the operator which part of the form is still empty, while the saved
 * verdict (support ending soon, review overdue, …) comes back from the server.
 */

import type { ChipTone } from '../../components/Chip';
import type {
  CraKategorie,
  ProduktVerantwortungValues,
  PsirtState,
  PsirtTeamValues,
} from '../../lib/types';

export const PSIRT_STATE_LABELS: Readonly<Record<PsirtState, string>> = {
  ok: 'Vollständig',
  warnung: 'Hinweise',
  fehler: 'Lücken',
};

export const PSIRT_STATE_TONE: Readonly<Record<PsirtState, ChipTone>> = {
  ok: 'good',
  warnung: 'warn',
  fehler: 'alert',
};

export const PSIRT_STATE_RANK: Readonly<Record<PsirtState, number>> = {
  fehler: 0,
  warnung: 1,
  ok: 2,
};

export const KATEGORIE_SHORT: Readonly<Record<CraKategorie, string>> = {
  STANDARD: 'Standard',
  WICHTIG_I: 'Wichtig I',
  WICHTIG_II: 'Wichtig II',
  KRITISCH: 'Kritisch',
};

export const KATEGORIE_TONE: Readonly<Record<CraKategorie, ChipTone>> = {
  STANDARD: 'neutral',
  WICHTIG_I: 'info',
  WICHTIG_II: 'warn',
  KRITISCH: 'alert',
};

/** Where the category is defined — the subtitle of the choice cards. */
export const KATEGORIE_FUNDSTELLE: Readonly<Record<CraKategorie, string>> = {
  STANDARD: 'nicht in Anhang III/IV gelistet',
  WICHTIG_I: 'Anhang III, Klasse I',
  WICHTIG_II: 'Anhang III, Klasse II',
  KRITISCH: 'Anhang IV',
};

export const KATEGORIE_ORDER: readonly CraKategorie[] = ['STANDARD', 'WICHTIG_I', 'WICHTIG_II', 'KRITISCH'];

/** EU-27, ISO 3166-1 alpha-2 (Greece as GR, not the EU-internal EL). */
export const EU_STATES: readonly { code: string; name: string }[] = [
  { code: 'BE', name: 'Belgien' },
  { code: 'BG', name: 'Bulgarien' },
  { code: 'DK', name: 'Dänemark' },
  { code: 'DE', name: 'Deutschland' },
  { code: 'EE', name: 'Estland' },
  { code: 'FI', name: 'Finnland' },
  { code: 'FR', name: 'Frankreich' },
  { code: 'GR', name: 'Griechenland' },
  { code: 'IE', name: 'Irland' },
  { code: 'IT', name: 'Italien' },
  { code: 'HR', name: 'Kroatien' },
  { code: 'LV', name: 'Lettland' },
  { code: 'LT', name: 'Litauen' },
  { code: 'LU', name: 'Luxemburg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Niederlande' },
  { code: 'AT', name: 'Österreich' },
  { code: 'PL', name: 'Polen' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Rumänien' },
  { code: 'SE', name: 'Schweden' },
  { code: 'SK', name: 'Slowakei' },
  { code: 'SI', name: 'Slowenien' },
  { code: 'ES', name: 'Spanien' },
  { code: 'CZ', name: 'Tschechien' },
  { code: 'HU', name: 'Ungarn' },
  { code: 'CY', name: 'Zypern' },
];

/**
 * Prefill for the coordinator CSIRT. Only where the designation is settled —
 * everything else stays a free-text field rather than a guess.
 */
export const CSIRT_SUGGESTIONS: Readonly<Record<string, string>> = {
  DE: 'BSI (Bundesamt für Sicherheit in der Informationstechnik)',
};

export const ERREICHBARKEIT_SUGGESTIONS: readonly string[] = [
  '24/7 Rufbereitschaft',
  'Mo–Fr 8–18 Uhr, außerhalb Rufbereitschaft',
  'Mo–Fr 8–17 Uhr, Wochenende per Eskalation',
];

export function stateName(code: string | null | undefined): string {
  if (!code) return '';
  return EU_STATES.find((state) => state.code === code)?.name ?? code;
}

export function filled(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

/** CMDBuild dates may carry a time part; `<input type="date">` wants the day only. */
export function dayValue(value: unknown): string {
  if (!filled(value)) return '';
  return String(value).slice(0, 10);
}

export function todayIso(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function ymd(iso: string): [number, number, number] {
  const [y = 1970, m = 1, d = 1] = iso.slice(0, 10).split('-').map(Number);
  return [y, m, d];
}

/** `YYYY-MM-DD` plus whole years, clamped to the end of February when needed. */
export function addYears(iso: string, years: number): string {
  const [y, m, d] = ymd(iso);
  const lastDay = new Date(Date.UTC(y + years, m, 0)).getUTCDate();
  return new Date(Date.UTC(y + years, m - 1, Math.min(d, lastDay))).toISOString().slice(0, 10);
}

/** Whole months from `start` to `end` (negative when `end` lies before). */
export function monthsBetween(start: string, end: string): number {
  const [y1, m1, d1] = ymd(start);
  const [y2, m2, d2] = ymd(end);
  let months = (y2 - y1) * 12 + (m2 - m1);
  if (d2 < d1) months -= 1;
  return months;
}

/** "5 J. 3 Mon." — compact, for table cells. */
export function fmtSpan(months: number): string {
  const abs = Math.abs(months);
  const years = Math.floor(abs / 12);
  const rest = abs % 12;
  if (years === 0) return `${rest} Mon.`;
  return rest === 0 ? `${years} J.` : `${years} J. ${rest} Mon.`;
}

export function fmtDayIso(value: string | null | undefined): string {
  if (!filled(value)) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

export interface ChecklistItem {
  key: string;
  label: string;
  reference: string;
  done: boolean;
  /** Recommended rather than mandatory — rendered as "empfohlen". */
  optional?: boolean;
  /** Satisfied by the team rather than by this form. */
  inherited?: boolean;
}

/** What the product form covers, in the order of the server's catalogue. */
export function productChecklist(
  values: ProduktVerantwortungValues,
  team: PsirtTeamValues | null,
): ChecklistItem[] {
  const hasTeam = team !== null;
  return [
    {
      key: 'team',
      label: 'PSIRT-Team zugeordnet',
      reference: 'Art. 13 Abs. 1',
      done: hasTeam,
    },
    {
      key: 'meldestelle',
      label: 'Meldestelle für Schwachstellen',
      reference: 'Art. 13 Abs. 17',
      done: hasTeam && filled(team?.MeldeEmail),
      inherited: true,
    },
    {
      key: 'cvd',
      label: 'CVD-Policy veröffentlicht',
      reference: 'Anh. I Teil II Nr. 5',
      done: hasTeam && filled(team?.CvdPolicyUrl),
      inherited: true,
    },
    {
      key: 'meldeweg',
      label: 'Meldeweg an CSIRT / ENISA',
      reference: 'Art. 14',
      done:
        hasTeam &&
        filled(team?.Mitgliedstaat) &&
        filled(team?.CsirtKoordinator) &&
        filled(team?.MelderEmail),
      inherited: true,
    },
    {
      key: 'owner',
      label: 'Product Security Owner',
      reference: 'Art. 13 Abs. 8',
      done: filled(values.OwnerName) && filled(values.OwnerEmail),
    },
    {
      key: 'vertretung',
      label: 'Stellvertretung',
      reference: 'Art. 14 Abs. 2',
      done: filled(values.VertretungName) || filled(team?.VertretungName),
      optional: true,
    },
    {
      key: 'kategorie',
      label: 'Produktkategorie eingestuft',
      reference: 'Anh. III/IV',
      done: filled(values.CraKategorie),
    },
    {
      key: 'support',
      label: 'Supportzeitraum festgelegt',
      reference: 'Art. 13 Abs. 8',
      done: filled(values.SupportEnde),
    },
    {
      key: 'updates',
      label: 'Update-Bereitstellung beschrieben',
      reference: 'Anh. I Teil II Nr. 7–8',
      done: filled(values.UpdateKanal),
      optional: true,
    },
    {
      key: 'review',
      label: 'Angaben geprüft',
      reference: 'Art. 13 Abs. 2',
      done: filled(values.GeprueftAm),
      optional: true,
    },
  ];
}

export function teamChecklist(values: PsirtTeamValues): ChecklistItem[] {
  return [
    {
      key: 'meldestelle',
      label: 'Meldestelle (öffentliche Adresse)',
      reference: 'Art. 13 Abs. 17',
      done: filled(values.MeldeEmail),
    },
    {
      key: 'cvd',
      label: 'CVD-Policy verlinkt',
      reference: 'Anh. I Teil II Nr. 5',
      done: filled(values.CvdPolicyUrl),
    },
    {
      key: 'meldeweg',
      label: 'Meldeweg an CSIRT / ENISA',
      reference: 'Art. 14 Abs. 7',
      done: filled(values.Mitgliedstaat) && filled(values.CsirtKoordinator) && filled(values.MelderEmail),
    },
    {
      key: 'erreichbarkeit',
      label: 'Erreichbarkeit für 24-h-Frühwarnung',
      reference: 'Art. 14 Abs. 2',
      done: filled(values.Erreichbarkeit),
      optional: true,
    },
    {
      key: 'leitung',
      label: 'Leitung und Stellvertretung',
      reference: 'Anh. I Teil II',
      done: filled(values.LeitungName) && filled(values.VertretungName),
      optional: true,
    },
  ];
}

/**
 * Submit body for the generic CRUD: every known key, empty strings for "clear".
 * Trimming happens server-side.
 */
export function toBody<T extends object>(values: T, keys: readonly (keyof T)[]): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const key of keys) {
    const raw = values[key];
    body[key as string] = raw === null || raw === undefined ? '' : raw;
  }
  return body;
}

export const TEAM_KEYS: readonly (keyof PsirtTeamValues)[] = [
  'Name',
  'MeldeEmail',
  'Telefon',
  'Erreichbarkeit',
  'CvdPolicyUrl',
  'SecurityTxtUrl',
  'Mitgliedstaat',
  'CsirtKoordinator',
  'MelderName',
  'MelderEmail',
  'LeitungName',
  'LeitungEmail',
  'VertretungName',
  'VertretungEmail',
  'Bemerkung',
];

export const PRODUCT_KEYS: readonly (keyof ProduktVerantwortungValues)[] = [
  'PsirtTeamID',
  'OwnerName',
  'OwnerEmail',
  'VertretungName',
  'VertretungEmail',
  'CraKategorie',
  'SupportEnde',
  'UpdateKanal',
  'GeprueftAm',
  'Bemerkung',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Light client-side format check; the server stays the authority on required fields. */
export function emailProblem(value: unknown): string | null {
  if (!filled(value)) return null;
  return EMAIL_RE.test(String(value).trim()) ? null : 'Keine gültige E-Mail-Adresse';
}

export function urlProblem(value: unknown): string | null {
  if (!filled(value)) return null;
  return /^https?:\/\/\S+\.\S+/i.test(String(value).trim()) ? null : 'Bitte mit https:// angeben';
}
